export type HttpErrorKind =
  | 'badRequest'
  | 'unauthorized'
  | 'forbidden'
  | 'notFound'
  | 'conflict'
  | 'server'
  | 'network'
  | 'unexpected'

export interface BackendValidationError {
  code: string
  incorrectValue: string
  details: string
}

export interface HttpErrorPresentation {
  kind: HttpErrorKind
  validationErrors?: BackendValidationError[]
}

type Translate = (key: string) => string
type TranslationExists = (key: string) => boolean

type UnknownRecord = Record<string, unknown>

function isRecord (value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null
}

function getValidationErrors (data: unknown) {
  if (!Array.isArray(data)) return undefined

  const validationErrors = data.flatMap(item => {
    if (!isRecord(item)) return []

    const { code, incorrectValue, details } = item
    if (
      typeof code !== 'string' ||
      typeof incorrectValue !== 'string' ||
      typeof details !== 'string'
    ) {
      return []
    }

    return [{ code, incorrectValue, details }]
  })

  return validationErrors.length > 0 ? validationErrors : undefined
}

function getValidationErrorKind (
  validationErrors: BackendValidationError[] | undefined
): HttpErrorKind | undefined {
  const codes = new Set(validationErrors?.map(({ code }) => code))

  if (codes.has('not-enough-permissions')) return 'forbidden'
  if (codes.has('doesnt-exist')) return 'notFound'
  if (codes.has('already-exist') || codes.has('already-added')) return 'conflict'

  return undefined
}

export function getSafeValidationDescription (
  validationErrors: BackendValidationError[] | undefined,
  translate: Translate,
  translationExists: TranslationExists
) {
  if (!validationErrors) return undefined

  const messages = validationErrors.flatMap(({ incorrectValue, details }) => {
    const detailsKey = `backend_messages.${details}`
    if (!translationExists(detailsKey)) return []

    const fieldKey = `backend_messages.fields.${incorrectValue}`
    const translatedDetails = translate(detailsKey)

    return [translationExists(fieldKey)
      ? `${translate(fieldKey)}: ${translatedDetails}`
      : translatedDetails]
  })

  return messages.length > 0 ? messages.join('\n') : undefined
}

export function getHttpErrorPresentation (
  error: unknown
): HttpErrorPresentation | null {
  if (!isRecord(error) || error.isAxiosError !== true) {
    return { kind: 'unexpected' }
  }

  if (error.code === 'ERR_CANCELED') return null

  const response = isRecord(error.response) ? error.response : undefined
  if (!response) return { kind: 'network' }

  const status = response.status
  const validationErrors = getValidationErrors(response.data)

  switch (status) {
    case 400:
      return {
        kind: getValidationErrorKind(validationErrors) || 'badRequest',
        validationErrors
      }
    case 401:
      return { kind: 'unauthorized' }
    case 403:
      return { kind: 'forbidden' }
    case 404:
      return { kind: 'notFound' }
    case 409:
      return { kind: 'conflict', validationErrors }
    default:
      return typeof status === 'number' && status >= 500
        ? { kind: 'server' }
        : { kind: 'unexpected' }
  }
}
